import { useEffect, useState } from "react";
import { useUser, SignUpButton } from "@clerk/clerk-react";
import {
  ArrowRight, Linkedin, Globe, Calendar, Mail, Phone, Edit3,
  BarChart3, MousePointer, MapPin, Smartphone, Eye, Plus,
  Check, ChevronDown,
} from "lucide-react";
import Button from "../ui/Button";
import HeroHandlePicker from "../HeroHandlePicker";

// Variant D — Guided tour.
// Card front-and-center. Hover any element to see a popover explaining the
// capability + a Claim CTA. Below: a hoverable analytics dashboard mock so
// visitors literally see what they'd get. Every popover ends with a sign-up
// nudge.

type HotSpotKey =
  | "name"
  | "title"
  | "url"
  | "linkedin"
  | "calendar"
  | "portfolio"
  | "addLink"
  | "stats"
  | "topLinks"
  | "devices"
  | "geo"
  | null;

interface HotSpotMeta {
  title: string;
  body: string;
  cta: string;
}

const HOTSPOTS: Record<Exclude<HotSpotKey, null>, HotSpotMeta> = {
  name: {
    title: "Edit your name and avatar",
    body: "Switch jobs? Adopt a new last name? Update once. Every share you've ever sent updates with it.",
    cta: "Claim my link",
  },
  title: {
    title: "Title and company, anytime",
    body: "Got promoted Tuesday? Update Tuesday. The link your network has stays the same.",
    cta: "Claim my link",
  },
  url: {
    title: "Your one URL, forever",
    body: "Pick the slug now and we'll keep it pointed at whichever card you mark active. Pro lets you keep up to three cards under one handle.",
    cta: "Claim my link",
  },
  linkedin: {
    title: "Drop in any link people ask for",
    body: "LinkedIn, Calendly, portfolio, deck, payment, podcast, Spotify, GitHub. Reorder them anytime. Up to 10 on Pro.",
    cta: "See all link presets",
  },
  calendar: {
    title: "Calendar booking front-and-center",
    body: "Calendly, Cal.com, or Microsoft Bookings. Less email back-and-forth. The most-clicked link on most cards.",
    cta: "Try it free",
  },
  portfolio: {
    title: "Custom links with previews",
    body: "Paste any URL — we fetch the title, description, and preview image automatically. Custom icons too.",
    cta: "Try it free",
  },
  addLink: {
    title: "Add 5 links free, 10 on Pro",
    body: "Reorder by drag, group by category, hide and show with a toggle. Always editable.",
    cta: "Start free",
  },
  stats: {
    title: "Free analytics for every card",
    body: "Total visits, weekly trend, unique visitors. No third-party tracker. Pro adds per-link click charts and date filtering.",
    cta: "See it live",
  },
  topLinks: {
    title: "Per-link click counts",
    body: "Know what's actually working. Pro adds a 7-day, 3-month, 6-month, and yearly chart per link.",
    cta: "Get analytics",
  },
  devices: {
    title: "Device + browser breakdown",
    body: "iPhone vs Android, Safari vs Chrome. Useful for sales debugging trade-show conversions.",
    cta: "Get analytics",
  },
  geo: {
    title: "Where the visits came from",
    body: "City and country breakdown. Helpful for measuring which events drove what.",
    cta: "Get analytics",
  },
};

const FAQ = [
  { q: "Do I need an app?", a: "No. The recipient just opens the link." },
  { q: "What if my title changes?", a: "Update the card. Every share updates with it." },
  { q: "Free tier?", a: "Yes — 1 card, 5 links, basic analytics, forever." },
  { q: "How does the NFC card work?", a: "Hold it near a phone. The card opens. Has a QR backup." },
];

export default function LandingGuided() {
  const { isSignedIn } = useUser();
  const [hovered, setHovered] = useState<HotSpotKey>(null);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  useEffect(() => {
    document.title = "WhoBrew — One link, every link people ask for";
  }, []);

  const meta = hovered ? HOTSPOTS[hovered] : null;

  return (
    <div className="bg-white text-coffee-900">
      {/* HERO — card front-and-center */}
      <section className="relative overflow-hidden border-b border-coffee-100">
        <div className="absolute inset-0 bg-dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-12 sm:pb-16">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="eyebrow mb-4">Hover the card to explore · free to start</div>
            <h1 className="text-display text-[44px] sm:text-[64px] md:text-[80px] leading-[1] tracking-[-0.02em] text-coffee-900">
              This is your card.
              <br />
              <span className="text-coffee-700">Hover anything.</span>
            </h1>
            <p className="mt-5 text-lg text-coffee-700 max-w-xl mx-auto">
              One link with everything someone could ask for. Share by URL or QR, edit anything,
              see who looked. Optional NFC card adds tap-to-open.
            </p>
          </div>

          {/* Card + popover side-by-side */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start max-w-5xl mx-auto">
            {/* CARD */}
            <div className="lg:col-span-7 lg:col-start-2">
              <div className="relative max-w-md mx-auto">
                <div className="absolute -inset-6 bg-coffee-100 rounded-[2.5rem] blur-3xl opacity-50 pointer-events-none" />
                <div className="relative bg-white border border-coffee-100 rounded-2xl shadow-coffee-lg overflow-hidden">
                  <div className="bg-gradient-to-br from-coffee-700 to-coffee-900 h-28" />
                  <div className="text-center -mt-12 px-6">
                    {/* Avatar / Name */}
                    <HotSpot id="name" hovered={hovered} setHovered={setHovered} className="inline-block">
                      <div className="w-24 h-24 bg-coffee-700 rounded-full mx-auto mb-3 flex items-center justify-center ring-4 ring-white shadow">
                        <span className="text-white text-2xl font-bold">ST</span>
                      </div>
                      <h3 className="text-xl font-bold text-coffee-900 inline-flex items-center gap-1.5">
                        Sean Thomas
                        <Edit3 className="w-3.5 h-3.5 text-coffee-400" />
                      </h3>
                    </HotSpot>

                    <HotSpot id="title" hovered={hovered} setHovered={setHovered}>
                      <p className="text-coffee-600 text-sm">Founder &amp; CEO · WhoBrew</p>
                    </HotSpot>

                  </div>

                  <div className="px-5 pb-5 mt-5 space-y-2">
                    <HotSpot id="linkedin" hovered={hovered} setHovered={setHovered} block>
                      <CardLinkRow icon={<Linkedin className="w-4 h-4" />} title="LinkedIn" desc="Connect with me" />
                    </HotSpot>
                    <HotSpot id="calendar" hovered={hovered} setHovered={setHovered} block>
                      <CardLinkRow icon={<Calendar className="w-4 h-4" />} title="Book a meeting" desc="Schedule a call" />
                    </HotSpot>
                    <HotSpot id="portfolio" hovered={hovered} setHovered={setHovered} block>
                      <CardLinkRow icon={<Globe className="w-4 h-4" />} title="Portfolio" desc="View my work" />
                    </HotSpot>
                    <HotSpot id="addLink" hovered={hovered} setHovered={setHovered} block>
                      <div className="flex items-center justify-center gap-2 px-3 py-3 rounded-xl border border-dashed border-coffee-300 text-coffee-600 hover:bg-coffee-50 transition-colors text-sm">
                        <Plus className="w-4 h-4" /> Add another link
                      </div>
                    </HotSpot>
                  </div>
                </div>
              </div>
            </div>

            {/* POPOVER PANEL */}
            <div className="lg:col-span-3 lg:sticky lg:top-24">
              <div className={`bg-white border border-coffee-100 rounded-2xl p-6 shadow-coffee transition-opacity duration-200 ${meta ? "opacity-100" : "opacity-70"}`}>
                {meta ? (
                  <>
                    <div className="eyebrow mb-2">Hover state</div>
                    <h3 className="text-display text-xl text-coffee-900 mb-2">{meta.title}</h3>
                    <p className="text-sm text-coffee-700 leading-relaxed mb-4">{meta.body}</p>
                    {isSignedIn ? (
                      <Button to="/my-card" fullWidth size="md" trailingIcon={<ArrowRight className="w-4 h-4" />}>
                        Open my card
                      </Button>
                    ) : (
                      <SignUpButton mode="modal">
                        <Button fullWidth size="md" trailingIcon={<ArrowRight className="w-4 h-4" />}>
                          {meta.cta}
                        </Button>
                      </SignUpButton>
                    )}
                  </>
                ) : (
                  <>
                    <div className="eyebrow mb-2">Hover anything</div>
                    <h3 className="text-display text-lg text-coffee-900 mb-2">
                      Hover the card to learn about each piece.
                    </h3>
                    <p className="text-sm text-coffee-600 leading-relaxed">
                      The avatar, the title, the URL, every link, the &quot;add another link&quot; tile —
                      each one is editable. Hover to see how.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Hero CTA + handle picker */}
          <div className="max-w-2xl mx-auto mt-16">
            <HeroHandlePicker origin={typeof window !== "undefined" ? window.location.origin : "https://whobrew.us"} />
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-coffee-500">
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-coffee-600" strokeWidth={3} /> No credit card</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-coffee-600" strokeWidth={3} /> 60-second setup</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-coffee-600" strokeWidth={3} /> Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* ANALYTICS DASHBOARD WALK-THROUGH */}
      <section className="py-20 sm:py-28 bg-coffee-50 border-b border-coffee-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="eyebrow mb-3">What you see after every event</div>
            <h2 className="text-display text-3xl sm:text-5xl text-coffee-900 leading-[1.05]">
              Free analytics. Hover the dashboard.
            </h2>
            <p className="mt-4 text-coffee-700 text-lg">
              No third-party tracker. No cookies. Just your data.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 max-w-6xl mx-auto items-start">
            {/* DASHBOARD MOCK */}
            <div className="lg:col-span-9">
              <div className="rounded-2xl border border-coffee-200 bg-white p-2 shadow-coffee-lg">
                <div className="flex items-center gap-1.5 px-2 py-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-coffee-200" />
                  <span className="w-2.5 h-2.5 rounded-full bg-coffee-200" />
                  <span className="w-2.5 h-2.5 rounded-full bg-coffee-200" />
                  <span className="ml-3 text-[10px] font-mono text-coffee-400">whobrew.us/insights</span>
                  <span className="ml-auto text-[10px] font-mono text-coffee-400">Example data</span>
                </div>
                <div className="bg-white rounded-xl p-5 space-y-4">
                  {/* Stats row */}
                  <HotSpot id="stats" hovered={hovered} setHovered={setHovered} block>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { k: "Total visits", v: "1,284", trend: "+18%" },
                        { k: "Unique visitors", v: "892", trend: "+12%" },
                        { k: "Saved contact", v: "147", trend: "+24%" },
                      ].map((s) => (
                        <div key={s.k} className="rounded-xl bg-coffee-50 border border-coffee-100 p-3.5">
                          <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-500">{s.k}</p>
                          <p className="text-2xl font-mono text-coffee-900 mt-1">{s.v}</p>
                          <p className="text-[10px] text-emerald-600 mt-0.5">{s.trend} this week</p>
                        </div>
                      ))}
                    </div>
                  </HotSpot>

                  {/* Chart */}
                  <div className="rounded-xl bg-coffee-50 border border-coffee-100 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-500">Visits · last 14 days</p>
                      <p className="text-[10px] font-mono text-coffee-400">7d / 3m / 6m / 1y</p>
                    </div>
                    <div className="h-28 flex items-end gap-1.5">
                      {[28, 32, 24, 48, 36, 64, 56, 80, 72, 92, 84, 100, 88, 96].map((h, i) => (
                        <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-coffee-700 to-coffee-500" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                  </div>

                  {/* Top links + breakdowns */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <HotSpot id="topLinks" hovered={hovered} setHovered={setHovered} block>
                      <div className="rounded-xl bg-coffee-50 border border-coffee-100 p-3.5 h-full">
                        <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-500 mb-2.5">Top links</p>
                        <div className="space-y-1.5">
                          {[
                            { i: <Calendar className="w-3 h-3" />, t: "Book a meeting", v: 87 },
                            { i: <Linkedin className="w-3 h-3" />, t: "LinkedIn", v: 64 },
                            { i: <Globe className="w-3 h-3" />, t: "Portfolio", v: 41 },
                          ].map((l) => (
                            <div key={l.t} className="flex items-center gap-2 text-xs text-coffee-800">
                              <span className="text-coffee-500">{l.i}</span>
                              <span className="flex-1 truncate">{l.t}</span>
                              <span className="font-mono text-coffee-500">{l.v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </HotSpot>
                    <HotSpot id="devices" hovered={hovered} setHovered={setHovered} block>
                      <div className="rounded-xl bg-coffee-50 border border-coffee-100 p-3.5 h-full">
                        <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-500 mb-2.5">Devices</p>
                        <div className="space-y-1.5">
                          {[
                            { t: "iPhone", v: 64 },
                            { t: "Android", v: 28 },
                            { t: "Desktop", v: 8 },
                          ].map((l) => (
                            <div key={l.t} className="flex items-center gap-2 text-xs text-coffee-800">
                              <span className="flex-1">{l.t}</span>
                              <span className="font-mono text-coffee-500">{l.v}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </HotSpot>
                    <HotSpot id="geo" hovered={hovered} setHovered={setHovered} block>
                      <div className="rounded-xl bg-coffee-50 border border-coffee-100 p-3.5 h-full">
                        <p className="text-[10px] tracking-eyebrow font-semibold text-coffee-500 mb-2.5">Cities</p>
                        <div className="space-y-1.5">
                          {[
                            { t: "Tuscaloosa, AL", v: 412 },
                            { t: "New York, NY", v: 188 },
                            { t: "Austin, TX", v: 96 },
                          ].map((l) => (
                            <div key={l.t} className="flex items-center gap-2 text-xs text-coffee-800">
                              <span className="flex-1 truncate">{l.t}</span>
                              <span className="font-mono text-coffee-500">{l.v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </HotSpot>
                  </div>
                </div>
              </div>
            </div>

            {/* DASHBOARD popover panel */}
            <div className="lg:col-span-3 lg:sticky lg:top-24">
              <div className={`bg-white border border-coffee-100 rounded-2xl p-6 shadow-coffee transition-opacity duration-200 ${meta ? "opacity-100" : "opacity-70"}`}>
                {meta ? (
                  <>
                    <div className="eyebrow mb-2">Hover state</div>
                    <h3 className="text-display text-xl text-coffee-900 mb-2">{meta.title}</h3>
                    <p className="text-sm text-coffee-700 leading-relaxed mb-4">{meta.body}</p>
                    {isSignedIn ? (
                      <Button to="/insights" fullWidth size="md" trailingIcon={<ArrowRight className="w-4 h-4" />}>
                        See my insights
                      </Button>
                    ) : (
                      <SignUpButton mode="modal">
                        <Button fullWidth size="md" trailingIcon={<ArrowRight className="w-4 h-4" />}>
                          {meta.cta}
                        </Button>
                      </SignUpButton>
                    )}
                  </>
                ) : (
                  <>
                    <div className="eyebrow mb-2">Free analytics</div>
                    <h3 className="text-display text-lg text-coffee-900 mb-2">
                      Hover the dashboard.
                    </h3>
                    <p className="text-sm text-coffee-600 leading-relaxed">
                      The numbers, the chart, the top-links list, devices, geo — every block shown
                      here is a live feature, and your own numbers appear once you sign up. Figures
                      shown are examples.
                    </p>
                    <div className="mt-4">
                      {isSignedIn ? (
                        <Button to="/insights" fullWidth size="sm" variant="secondary">Open my insights</Button>
                      ) : (
                        <SignUpButton mode="modal">
                          <Button fullWidth size="sm">Get my dashboard</Button>
                        </SignUpButton>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Lower row — what we collect, plain English */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10 max-w-5xl mx-auto">
            {[
              { icon: <MousePointer className="w-4 h-4" />, title: "Every visit", body: "Total + unique counts, time of day, day of week." },
              { icon: <Smartphone className="w-4 h-4" />, title: "Device + browser", body: "iPhone vs Android, Safari vs Chrome." },
              { icon: <MapPin className="w-4 h-4" />, title: "City + country", body: "Coarse location for figuring out which event drove what." },
            ].map((d) => (
              <div key={d.title} className="bg-white border border-coffee-100 rounded-2xl p-5">
                <div className="w-9 h-9 rounded-lg bg-coffee-50 border border-coffee-100 flex items-center justify-center mb-3 text-coffee-700">
                  {d.icon}
                </div>
                <h3 className="text-coffee-900 font-semibold text-sm">{d.title}</h3>
                <p className="mt-1 text-xs text-coffee-600 leading-relaxed">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section className="py-20 sm:py-24 border-b border-coffee-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-display text-3xl sm:text-5xl text-coffee-900">Ready to claim yours?</h2>
          <p className="mt-3 text-coffee-700">Pick a name. Add some links. Share the URL. Done.</p>
          <div className="mt-7 max-w-xl mx-auto">
            <HeroHandlePicker origin={typeof window !== "undefined" ? window.location.origin : "https://whobrew.us"} />
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 sm:py-24 border-b border-coffee-100 bg-coffee-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <div className="eyebrow mb-3">Free with every account</div>
            <h2 className="text-display text-3xl sm:text-5xl text-coffee-900">In your dashboard, day one.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { i: <Edit3 className="w-4 h-4" />, t: "Edit anything", b: "Name, title, links, theme" },
              { i: <Eye className="w-4 h-4" />, t: "Visit analytics", b: "Per-card and per-link" },
              { i: <Mail className="w-4 h-4" />, t: "Lead capture", b: "Visitor info → /leads" },
              { i: <Phone className="w-4 h-4" />, t: "Save contact", b: "vCard download for any visitor" },
            ].map((f) => (
              <div key={f.t} className="bg-white border border-coffee-100 rounded-2xl p-5">
                <div className="w-9 h-9 rounded-lg bg-coffee-50 border border-coffee-100 flex items-center justify-center mb-3 text-coffee-700">
                  {f.i}
                </div>
                <p className="text-coffee-900 font-semibold text-sm">{f.t}</p>
                <p className="mt-0.5 text-xs text-coffee-600">{f.b}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            {isSignedIn ? (
              <Button to="/my-card" size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>Open my card</Button>
            ) : (
              <SignUpButton mode="modal">
                <Button size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>Claim my link</Button>
              </SignUpButton>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="eyebrow mb-3">FAQ</div>
          <h2 className="text-display text-3xl sm:text-5xl text-coffee-900 mb-8">Common questions.</h2>
          <div className="space-y-2">
            {FAQ.map((item, i) => {
              const open = openFAQ === i;
              return (
                <div key={i} className="bg-white border border-coffee-100 rounded-xl">
                  <button onClick={() => setOpenFAQ(open ? null : i)} className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-coffee-50">
                    <span className="font-semibold text-coffee-900">{item.q}</span>
                    <ChevronDown className={`w-5 h-5 text-coffee-500 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && <div className="px-5 pb-4 text-sm text-coffee-700 leading-relaxed">{item.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 sm:py-32 bg-coffee-900 border-t border-coffee-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-display text-4xl sm:text-6xl text-white">Pick a name. Get a link.</h2>
          <p className="mt-4 text-coffee-200 text-lg">Sixty seconds. Free. No card to reprint.</p>
          <div className="mt-8 flex justify-center">
            {isSignedIn ? (
              <Button to="/my-card" variant="secondary" size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>Open my card</Button>
            ) : (
              <SignUpButton mode="modal">
                <Button variant="secondary" size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>Claim my link</Button>
              </SignUpButton>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

/* Hover wrapper. On hover, sets the active hotspot key. Highlights on hover
   so visitors know it's interactive. `block` makes the wrapper fill its
   container (used for full-row link rows + dashboard tiles). */
function HotSpot({
  id,
  hovered,
  setHovered,
  className = "",
  block = false,
  children,
}: {
  id: Exclude<HotSpotKey, null>;
  hovered: HotSpotKey;
  setHovered: (k: HotSpotKey) => void;
  className?: string;
  block?: boolean;
  children: React.ReactNode;
}) {
  const active = hovered === id;
  return (
    <div
      onMouseEnter={() => setHovered(id)}
      onMouseLeave={() => setHovered(null)}
      onFocus={() => setHovered(id)}
      onBlur={() => setHovered(null)}
      tabIndex={0}
      className={`relative rounded-xl transition-all duration-200 cursor-pointer ${block ? "block" : ""} ${active ? "ring-2 ring-coffee-500 ring-offset-2 ring-offset-white" : ""} ${className}`}
      aria-label={HOTSPOTS[id].title}
    >
      {children}
    </div>
  );
}

function CardLinkRow({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-coffee-50 border border-coffee-100 transition-colors">
      <div className="w-9 h-9 rounded-lg bg-white text-coffee-700 flex items-center justify-center border border-coffee-100">
        {icon}
      </div>
      <div className="flex-1 text-left">
        <p className="text-sm font-semibold text-coffee-900">{title}</p>
        <p className="text-xs text-coffee-500">{desc}</p>
      </div>
    </div>
  );
}
